'use client';

import Link from 'next/link';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormWrapper } from '../FormWrapper';
import { Label } from '@workspace/ui/components/label';
import { Input } from '@workspace/ui/components/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@workspace/ui/components/button';
import { GoogleButton, OrDivider } from '../GoogleButton';
import { forgotPassword, login } from '@/src/actions/auth';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Icon } from '@/src/components/ui/Icon';
import { Spinner } from '@/src/components/ui';
import { ILoginData } from 'types/src/types';
import { useRouter } from 'next/navigation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { identifyUser, trackLogin } from '@/src/lib/analytics/posthog';

const schema = z.object({
  email: z.string().nonempty('Email is required'),
  password: z.string().nonempty('Password is required'),
  remember: z.boolean().default(false),
});

export const LoginForm = () => {
  const router = useRouter();
  const isLoading = useBoolean(false);
  const isResetSent = useBoolean(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ILoginData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    },
  });
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');

  const onSubmit = async (values: ILoginData) => {
    isLoading.onTrue();
    try {
      const { data, error } = await login({
        email: values.email,
        password: values.password,
        remember: values.remember,
      });

      if (error && !data) {
        setError('email', {
          type: 'manual',
          message: 'Wrong email or password. Please try again.',
        });
        setError('password', {
          type: 'manual',
          message: 'Wrong email or password. Please try again.',
        });
        throw new Error(error.message);
      } else {
        trackLogin();
        identifyUser({ id: data.userId, email: data.email });

        router.push(`/${data.uniqueLink}`);
      }
    } catch (error: any) {
      isLoading.onFalse();
    } finally {
      isLoading.onFalse();
    }
  };

  const isFilled = !!watch('email') && !!watch('password');
  useLockBodyScroll(isDialogOpen);

  return (
    <div className="relative">
      <FormWrapper
        title="Welcome back!"
        description="Please enter your details."
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full  xl:md:px-28 lg:px-10"
        >
          <Label className="w-full relative flex flex-col items-start text-base">
            <Input
              type="email"
              className="border w-full rounded-md placeholder:text-gray-text md:placeholder:text-base md:h-[51px] h-10 font-light md:text-base text-xs"
              {...register('email')}
              placeholder="Enter your email"
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <p className="text-red-error md:text-sm text-xs font-light flex items-center gap-2.5 ">
                <Icon name="exclamation-mark" className="size-4" />
                {errors.email.message}
              </p>
            )}
          </Label>
          <Label className="w-full relative flex flex-col items-start text-base md:mt-3 mt-2">
            <Input
              type="password"
              className="border w-full rounded-md placeholder:text-gray-text md:placeholder:text-base md:h-[51px] h-10 font-light md:text-base text-xs"
              {...register('password')}
              placeholder="Enter your password"
              aria-invalid={!!errors.password}
            />
            {errors.password && (
              <p className="text-red-error md:text-sm text-xs font-light flex items-center gap-2.5 ">
                <Icon name="exclamation-mark" className="size-4" />
                {errors.password.message}
              </p>
            )}
          </Label>

          <div className="flex justify-between items-center md:mt-3 mt-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 mr-2 text-primary border-gray-text rounded focus:ring-primary"
                {...register('remember')}
              />
              <label
                htmlFor="remember"
                className="text-gray-text font-normal md:text-sm text-xs"
              >
                Remember for 30 days
              </label>
            </div>
          </div>

          <Button
            className="w-full md:mt-8 mt-5 md:h-[51px] h-10"
            disabled={!isFilled || isSubmitting || !!errors.password}
          >
            {isLoading.value ? <Spinner /> : 'Log in'}
          </Button>
          <OrDivider />
          <GoogleButton name="Sign in" />

          <div className="flex justify-center items-center md:mt-8 mt-5">
            <p className="text-gray-500 md:text-sm text-xs font-normal">
              Don't have an account?{' '}
              <Link
                href="/sign-up"
                className="text-blue-primary hover:text-button-primary-hover font-medium"
              >
                Sign up
              </Link>
            </p>
          </div>
        </form>
      </FormWrapper>
      <div className="flex justify-between items-center w-full xl:md:px-28 mt-2">
        <div />
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="link"
              className="text-gray-500 hover:text-gray-700 md:text-sm text-xs font-medium absolute md:right-[99px] md:top-[390px] lg:right-[30px] lg:top-[385px] xl:right-[99px] xl:top-[390px] -right-3 top-[246px]"
            >
              Forgot password?
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {isResetSent.value ? 'Check your email' : 'Enter your email'}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className={errorMessage ? 'text-red-500' : ''}>
              {isResetSent.value
                ? 'Password reset link has been sent to your email.'
                : !!errorMessage
                  ? errorMessage
                  : 'Please enter the email address associated with your account, and we will send you a link to reset your password.'}
            </DialogDescription>
            {!isResetSent.value && (
              <div className="w-full mt-4">
                <Input
                  type="email"
                  className="border w-full rounded-md placeholder:text-gray-text md:placeholder:text-base md:h-[51px] h-10 font-light md:text-base text-xs"
                  placeholder="Enter your email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                />
              </div>
            )}

            {!isResetSent.value && (
              <DialogFooter className="flex justify-between mt-4">
                <DialogTrigger asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogTrigger>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    isLoading.onTrue();
                    if (forgotPasswordEmail.trim() !== '') {
                      try {
                        const { error } =
                          await forgotPassword(forgotPasswordEmail);
                        if (error) {
                          setErrorMessage(error);
                          isResetSent.onFalse();
                        } else {
                          setErrorMessage('');
                          isResetSent.onTrue();
                        }
                      } finally {
                        isLoading.onFalse();
                      }
                    } else {
                      alert('Please enter your email address.');
                    }
                  }}
                  className="w-[153px]"
                >
                  {isLoading.value ? <Spinner /> : 'Reset password'}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
